import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, List, message, Modal, Select, Skeleton, Tooltip, Typography } from "antd";
import veloproAPI from "../../services/api";
import bolsaAPI from "../../services/bolsa-api";
import extAPI from "../../services/exterior-api";
import { IClient } from "../../contexts/ClientsContext";
import { cards } from "./items";

import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { download, isMobile, monthsExtended, zero } from "../../utils";
import { Card } from "./Cards";
import { Container, Content, ReportContainer } from "./styles";
import { RiDownloadCloudLine } from "react-icons/ri";

interface IReportsModalProps {
  open: boolean;
  type: string;
  clientSelected?: IClient;
  onCancel: () => void;
}

const ReportsModal: React.FC<IReportsModalProps> = ({
  open,
  type,
  clientSelected,
  onCancel
}) => {
  // Variáveis gerais
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [action, setAction] = useState<string>('');
  const [data, setData] = useState<any[]>();
  const api = (type == 'bolsa') ? bolsaAPI : extAPI;
  const getClientToken = async (client: IClient) => {
    if (!client || Object.keys(client).length === 0) return;
    const { data } = await veloproAPI.get(`/clients/token/${client?.username}`);
    api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    if (type == 'bolsa') api.defaults.headers.common["X-Api-Provider"] = `xpinvestimentos`;
    return ;
  };

  // Datas
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const [initialYear, setInitialYear] = useState(currentYear);
  const [year, setYear] = useState(initialYear);

  const transactionsFeature = { apiUrl: "/darf/report/transactions" };
  const downloadFeature = { apiUrl: "/darf/report/download" };
  const historicFeature = { apiUrl: "/velotax/exterior/historic" }
  const darfFeature = { apiUrl: "/darf" }

  const yearOptions = Array.from(Array(initialYear - 2020 + 2))
    .map((y, i) => ({
      value: 2020 + i,
      label: `${2020 + i}`,
    }))
    .reverse();

  const onChangeYear = (value: string) => {
    setYear(parseInt(value));
  };

  const handleActionButton = (id: string) => {
    setAction(id);
    switch (id) {
      case "anual-report":
      case "income":
        setYear(2023);
        setInitialYear(2023);
        break;
      case "gain":
        setYear(2024);
        setInitialYear(2024);
        break;
    }
  };

  const tooltipInfo: any = (info: string) => (
    <p
      style={{
        marginTop: "10px",
        fontSize: "13px",
        display: "flex",
        textAlign: "center",
      }}
    >
      {info}
    </p>
  );

  const downloadAction = async (record: any) => {
    const act = record?.action || action;
    setDownloading(true);

    const mobileAction = isMobile()

    if (mobileAction) {
      message.info("Preparando seu relatório.");
    } else {
      message.info("Iniciando download do seu relatório.");
    }
    api.post(`${downloadFeature.apiUrl}/${act}`, {
      from: "veloprime",
      id: record._id,
      month: record.month,
      year: record.year,
      // sendMail: record.sendMail || mobileAction,
    })
      .then((res) => {
        if (!res.data.fileUrl) {
          return message.error("Houve um erro ao buscar seu relatório!")
        }
        if (!mobileAction) {
          download(res.data.fileUrl);
        }
        // if (record.sendMail || mobileAction) {
        //   message.success("Relatório enviado para o seu e-mail");
        // } else {
        message.success("Relatório emitido com sucesso");
        // }
      })
      .catch(({ response }) =>
        message.error(response?.data?.message)
      )
      .finally(() => {
        setDownloading(false);
      });
  };

  const downloadDarf = async (data: any) => {
    setDownloading(true);

    api.post(`${darfFeature.apiUrl}`, data)
      .then((res) => {
        if (!res?.data?.darfUrl ) {
          return message.error("Houve um erro ao baixar seu relatório!")
        }
        download(res.data.darfUrl);
        message.success("Relatório emitido com sucesso");
      })
      .catch(({ response }) =>
        message.error(response?.data?.message)
      )
      .finally(() => {
        setDownloading(false);
      });
  };

  const checkProcessorAction = (record: any) => {
    setLoading(true);
    let showToast = false;
    const itvl = setInterval(() => {
      api.get(`${downloadFeature.apiUrl}/anual-report?year=${record.year}`)
        .then((res) => {
          const { data } = res;
          const { year, status } = data;
          const isProcessing = record.year === year && status === "processing";
          if (isProcessing && showToast) return;
          if (isProcessing && !showToast) {
            showToast = true;
            return message.success(
              "O seu Informe de Rendimentos está em geração. Aguarde alguns minutos."
            );
          }
          setLoading(false);
          clearInterval(itvl);
          downloadAction(record);
        })
        .catch(({ response }) => {
          const { data, status } = response;
          if (status === 401) message.error(data?.message);
          setLoading(false);
          clearInterval(itvl);
        });
    }, 5000);
  };

  const checkDarf = (record: any) => {
    setLoading(true);
    api.get(`${historicFeature.apiUrl}`, { params: { year: record.year} })
    .then((res) => {
      const darf = res.data.find((d: any)=> {
        if (record?.month == d?.month) return d;
      })
      if (!darf || !darf?._id) {
        message.info("Cliente não possui relatório para este mês.");
        setLoading(false);
        return;
      }
      delete darf?.transactions;
      const data = {
        ...darf,
        year: record.year,
        month: record.month,
        name: clientSelected?.name,
        memoriaCalculo: [],
        justSave: false,
        payed: false,
        noSendMail: true,
        paymentInfo: {
          clientInfo: {
            name: clientSelected?.name,
            email: clientSelected?.email,
            document: clientSelected?.username,
          },
        },
      };
      setLoading(false);
      downloadDarf(data);
    })
    .catch(({ response }) => {
      const { data, status } = response;
      if (status === 401) message.error(data?.message);
      setLoading(false);
    });
  }

    const getHistoric = useCallback(() => {
    if (!action) return;
    if (action === "anual-report")
      return setData(
        Array.from(Array(currentYear - 2022)) // -2022 para começar em 2022, sem +1 para não incluir ano atual
          .map((y, i) => ({
            year: 2022 + i,
            label: `${2022 + i}`,
          }))
          .reverse()
      );

    setLoading(true);
    api.get(`${transactionsFeature.apiUrl}${(type == 'exterior') ? `/${action}` : ''}`, { params: { year } })
      .then((response) => setData((response.data ?? []).reverse()))
      .catch(() => message.error("Algo inesperado aconteceu! Tente novamente mais tarde."))
      .finally(() => setLoading(false));
  }, [year, action]);

  useEffect(() => {
    if (clientSelected) getClientToken(clientSelected);
    getHistoric();
  }, [getHistoric, year]);

  useEffect(() => {
    if (!type) setAction('');
    if (type === 'exterior') setAction('anual-report');
  }, [type])

  const renderAction = () => {
    return (
      <>
        {action && (
          <Container>
            <Content>
              <Typography.Paragraph>Selecione o ano:</Typography.Paragraph>
              {action !== "anual-report" && (
                <Select
                  size="large"
                  options={yearOptions}
                  value={year.toString()}
                  onChange={onChangeYear}
                  placeholder="Selecione o ano"
                  style={{ minWidth: "150px" }}
                />
              )}
              <List
                loading={loading || downloading}
                dataSource={
                  data?.filter(
                    (item) =>
                      (action === "anual-report" && item.year !== 2025) ||
                      (action !== "anual-report" && item.year === year)
                      // (item.year === currentYear && action !== "anual-report")
                  ) ?? []
                }
                itemLayout="horizontal"
                renderItem={(item, index) => {
                  // const show2024Report = localStorage.getItem('show2024Report') === 'show';
                  // if (item.year === 2024 && action === "anual-report" && !show2024Report) return <></>
                  return (
                    <List.Item
                      actions={[
                        <Button
                          type="text"
                          size="large"
                          href={item.url}
                          disabled={loading || downloading}
                          onClick={
                            () =>
                              (action === "anual-report" && (type == "bolsa"))
                                ? checkProcessorAction(item)
                                : ((action === "gain") && type === "exterior" && year === 2024)
                                  ? checkDarf(item)
                                  : downloadAction(item)
                          }
                          download={`Relatório-${item.year}-${zero(
                            item.month
                          )}.pdf`}
                        >
                          Baixar
                          <RiDownloadCloudLine size={16} />
                        </Button>,
                      ]}
                    >
                      <Skeleton title={true} loading={item.loading} active>
                        <List.Item.Meta
                          title={
                            action !== "anual-report"
                              ? `${monthsExtended[item.month - 1]}`
                              : `${item.year}`
                          }
                        />
                      </Skeleton>
                    </List.Item>
                  )
                }}
              />
              <Button
                type="link"
                size="large"
                disabled={loading || downloading}
                onClick={() => {
                  // handleActionButton("")
                  onCancel()
                }}
                style={{ marginTop: "1rem" }}
              >
                Voltar
              </Button>
            </Content>
          </Container>
        )}
      </>
    )
  }

  return (
    <Modal
      footer={null}
      open={open}
      title={type === 'bolsa'? `Relatórios de ${type}`: 'Informe de rendimentos'}
      onCancel={onCancel}
      width={isMobile() ? '95%' : 600}
      centered
      style={{ top: isMobile() ? 20 : 100 }}
    >
      {!action ?
        <ReportContainer className="cards">
          {cards.map((item) => {
            return (
              <>
                {item.type.includes(type) &&
                  <Card
                    id={item.id}
                    key={item.id}
                    disabled={item.id !== "anual-report" && loading}
                    icon={
                      (loading || downloading) && item.id === "anual-report" ? (
                        <LoadingOutlined />
                      ) : (
                        item.icon
                      )
                    }
                    onClick={() => !loading && handleActionButton(item.id)}
                  >
                    <>
                      <Col style={{ position: "absolute", right: '20px', top: '20px' }}>
                        <Tooltip color="white" title={tooltipInfo(item.description)}>
                          <InfoCircleOutlined />
                        </Tooltip>
                      </Col>
                      {item.content}
                    </>
                  </Card>
                }
              </>
            )
          })}
        </ReportContainer>
        : renderAction()
      }
    </Modal>
  );
};

export default ReportsModal;
